import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate } from '@tanstack/react-router';
import { useId, useState, type FormEvent } from 'react';
import { loginAuthenticator, loginOptions, loginRecovery, loginVerify } from './api';
import { describeAuthError, askForPasskey } from './passkeys';
import { AUTH_STATE_KEY, useAuthState } from './useAuthState';

type Mode = 'passkey' | 'authenticator' | 'recovery';

export function SignInPage() {
  const id = useId();
  const state = useAuthState();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('passkey');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function finish() {
    await client.invalidateQueries({ queryKey: AUTH_STATE_KEY });
    await navigate({ to: '/' });
  }

  async function withPasskey() {
    setBusy(true);
    setError(null);
    try {
      const { challengeId, options } = await loginOptions();
      // This is the Face ID / Windows Hello prompt.
      const response = await askForPasskey(options);
      await loginVerify(challengeId, response);
      await finish();
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function withCode(e: FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'authenticator') await loginAuthenticator(code.trim());
      else await loginRecovery(code.trim());
      await finish();
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  function choose(next: Mode) {
    setMode(next);
    setCode('');
    setError(null);
  }

  if (state.data?.signedIn) return <Navigate to="/" replace />;

  let body;
  if (state.isPending) {
    body = (
      <section className="card" role="status" data-state="loading">
        <p className="card-title">Loading…</p>
      </section>
    );
  } else if (state.isError) {
    body = (
      <section className="card card-bad" role="status" data-state="unreachable">
        <p className="card-title">Can't reach the server</p>
        <button type="button" onClick={() => void state.refetch()}>
          Try again
        </button>
      </section>
    );
  } else if (!state.data.ownerExists) {
    body = (
      <section className="card" data-state="no-owner">
        <h2 className="card-heading">Not set up yet</h2>
        <p className="card-detail">Nobody has set up this Personal OS yet.</p>
        <Link to="/setup">Go to setup</Link>
      </section>
    );
  } else if (mode === 'passkey') {
    body = (
      <section className="card" data-state="passkey" aria-labelledby={`${id}-title`}>
        <h2 id={`${id}-title`} className="card-heading">
          Sign in
        </h2>
        <button
          type="button"
          className="primary big"
          disabled={busy}
          onClick={() => void withPasskey()}
        >
          {busy ? 'Waiting…' : 'Sign in with passkey'}
        </button>
        <p className="hint">Uses Face ID on iPhone, or Windows Hello on your computer.</p>
        {error && (
          <p role="alert" className="error-text">
            {error}
          </p>
        )}
        <p className="alt-links">
          <button type="button" className="link" onClick={() => choose('authenticator')}>
            Use authenticator app
          </button>
          <button type="button" className="link" onClick={() => choose('recovery')}>
            Use a recovery code
          </button>
        </p>
      </section>
    );
  } else {
    const isApp = mode === 'authenticator';
    body = (
      <section className="card" data-state={mode} aria-labelledby={`${id}-title`}>
        <h2 id={`${id}-title`} className="card-heading">
          {isApp ? 'Sign in with your authenticator app' : 'Sign in with a recovery code'}
        </h2>
        <form onSubmit={(e) => void withCode(e)} className="stack">
          <label htmlFor={id}>{isApp ? '6-digit code' : 'Recovery code'}</label>
          <input
            id={id}
            type="text"
            inputMode={isApp ? 'numeric' : 'text'}
            autoComplete={isApp ? 'one-time-code' : 'off'}
            autoCapitalize="characters"
            spellCheck={false}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {error && (
            <p role="alert" className="error-text">
              {error}
            </p>
          )}
          <button type="submit" className="primary" disabled={busy || code.trim() === ''}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
        </form>
        <p className="alt-links">
          <button type="button" className="link" onClick={() => choose('passkey')}>
            Back to passkey
          </button>
          <button
            type="button"
            className="link"
            onClick={() => choose(isApp ? 'recovery' : 'authenticator')}
          >
            {isApp ? 'Use a recovery code' : 'Use authenticator app'}
          </button>
        </p>
      </section>
    );
  }

  return (
    <main className="page">
      <h1>Personal OS</h1>
      {body}
    </main>
  );
}
